import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CreditCard,
  ShieldCheck,
  Sparkles,
  Store,
  Users,
  type LucideIcon,
} from "lucide-react";
import { OeloxLogo } from "../OeloxLogo";
import mascot from "../../assets/brand/oelox-mascot.png";
import { ROUTES } from "../../routes";

export type AuthHighlight = {
  icon: LucideIcon;
  title: string;
  body: string;
};

type AdminAuthLayoutProps = {
  title: string;
  subtitle: string;
  children: ReactNode;
  footerPrompt?: string;
  footerLinkLabel?: string;
  footerLinkTo?: string;
};

const HIGHLIGHTS: AuthHighlight[] = [
  {
    icon: Store,
    title: "Every restaurant",
    body: "Activate, suspend, and inspect credit wallets across the platform.",
  },
  {
    icon: CreditCard,
    title: "Order-credit billing",
    body: "Packages, deductions, and payment history with full audit trail.",
  },
  {
    icon: Users,
    title: "Dual customer model",
    body: "Global app users and restaurant CRM customers — kept separate.",
  },
];

export function AdminAuthLayout({
  title,
  subtitle,
  children,
  footerPrompt,
  footerLinkLabel,
  footerLinkTo,
}: AdminAuthLayoutProps) {
  return (
    <div className="auth-shell auth-shell--login">
      <aside className="auth-shell__hero" aria-hidden={false}>
        <div className="auth-shell__hero-glow" aria-hidden />
        <div className="auth-shell__hero-glow auth-shell__hero-glow--2" aria-hidden />
        <img src={mascot} alt="" aria-hidden className="auth-shell__mascot" />

        <div className="auth-shell__brand">
          <OeloxLogo variant="header" className="auth-shell__logo" />
        </div>

        <div className="auth-shell__hero-copy">
          <span className="auth-shell__badge">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            Super Admin Console
          </span>
          <h2 className="auth-shell__hero-title">
            Run the OELOX ecosystem from one control plane.
          </h2>
          <p className="auth-shell__hero-text">
            Credits, restaurants, customers, drivers, and revenue — built for platform operators.
          </p>

          <ul className="auth-shell__highlights">
            {HIGHLIGHTS.map(({ icon: Icon, title: hTitle, body }) => (
              <li key={hTitle} className="auth-shell__highlight">
                <span className="auth-shell__highlight-icon" aria-hidden>
                  <Icon className="h-4 w-4" strokeWidth={2.25} />
                </span>
                <div>
                  <p className="auth-shell__highlight-title">{hTitle}</p>
                  <p className="auth-shell__highlight-body">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="auth-shell__hero-foot">
          <ShieldCheck className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5" />
          Internal access only · provisioned manually
        </p>
      </aside>

      <main className="auth-shell__main">
        <div className="auth-shell__panel">
          <div className="auth-shell__mobile-brand">
            <OeloxLogo variant="dark" className="!mix-blend-normal h-8" />
          </div>

          <header className="auth-shell__header">
            <h1 className="auth-shell__title">{title}</h1>
            <p className="auth-shell__subtitle">{subtitle}</p>
          </header>

          <div className="auth-shell__card">{children}</div>

          {footerPrompt && footerLinkLabel && footerLinkTo ? (
            <p className="auth-shell__switch">
              {footerPrompt}{" "}
              <Link to={footerLinkTo} className="auth-shell__switch-link">
                {footerLinkLabel}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </Link>
            </p>
          ) : (
            <p className="auth-shell__switch">
              Need help?{" "}
              <Link to={ROUTES.LOGIN} className="auth-shell__switch-link">
                Back to sign in
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
